import logger from '@/utils/client-logger';
import { openDB as idbOpenDB, type DBSchema, type IDBPDatabase } from 'idb';

const DB_NAME = 'zeelool-store';
const STORE_NAME = 'kv';
const DB_VERSION = 1;

interface ZeeloolStoreDB extends DBSchema {
  kv: {
    key: string;
    value: unknown;
  };
}

let dbPromise: Promise<IDBPDatabase<ZeeloolStoreDB>> | null = null;

function openDB(): Promise<IDBPDatabase<ZeeloolStoreDB>> {
  if (dbPromise) return dbPromise;

  dbPromise = idbOpenDB<ZeeloolStoreDB>(DB_NAME, DB_VERSION, {
    upgrade(db) {
      try {
        if (!db.objectStoreNames.contains(STORE_NAME)) {
          db.createObjectStore(STORE_NAME);
        }
      } catch (error) {
        logger.error('IndexedDB upgrade exception:', error);
        throw error;
      }
    },
    terminated() {
      dbPromise = null;
    },
  });

  return dbPromise.catch((error) => {
    logger.error('IndexedDB open error:', error);
    dbPromise = null;
    throw error;
  });
}

export async function idbSet(key: string, value: any): Promise<void> {
  try {
    const db = await openDB();
    await db.put(STORE_NAME, value, key);
  } catch (error) {
    logger.error('idbSet failed for key:', key, error);
    throw error;
  }
}

export async function idbGet<T = any>(key: string): Promise<T | null> {
  try {
    const db = await openDB();
    const value = await db.get(STORE_NAME, key);
    return (value ?? null) as T | null;
  } catch (error) {
    logger.error('idbGet failed for key:', key, error);
    throw error;
  }
}

export async function idbRemove(key: string): Promise<void> {
  try {
    const db = await openDB();
    await db.delete(STORE_NAME, key);
  } catch (error) {
    logger.error('idbRemove failed for key:', key, error);
    throw error;
  }
}
